import { useState, type ChangeEvent } from 'react'
import { Link, createFileRoute } from '@tanstack/react-router'
import { SCRATCH_LIMITS, SCRATCH_SITE_TYPES, type ScratchInput, type ScratchProject } from '@hedgehog/shared'
import { api } from '@/lib/api'
import { opsApi, useOpsMutation } from '@/components/ops/api'
import { ErrorState, FormError, LoadingState } from '@/components/app/states'
import { formatDateTime } from '@/components/app/format'
import { Facts, Muted, Section } from '@/components/ops/section'
import { checkFile, fileUrl, formatBytes, IMAGE_ACCEPT, LOGO_ACCEPT, ScratchForm, uploadFile } from '@/components/ops/scratch'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'

export const Route = createFileRoute('/ops/scratch/$projectId')({
  loader: ({ params }) => opsApi<ScratchProject>(`/ops/scratch/${encodeURIComponent(params.projectId)}`),
  head: ({ loaderData }) => ({ meta: [{ title: `${loaderData?.businessName ?? 'Project'} · Ops` }] }),
  component: ScratchProjectPage,
  pendingComponent: () => <LoadingState label="Loading project" />,
  errorComponent: ({ error }) => <ErrorState error={error} title="Could not load the project" />,
})

function ScratchProjectPage() {
  const p = Route.useLoaderData()
  const draft = p.status === 'draft'
  const [editing, setEditing] = useState(false)
  const edit = useOpsMutation()
  const files = useOpsMutation()
  const [progress, setProgress] = useState<string | null>(null)
  const base = `/ops/scratch/${encodeURIComponent(p.id)}`

  async function save(value: ScratchInput) {
    const ok = await edit.run(() => api(base, { method: 'PATCH', json: value }).then(() => true))
    if (ok) setEditing(false)
  }

  async function onLogo(e: ChangeEvent<HTMLInputElement>) {
    const f = e.target.files?.[0]
    e.target.value = ''
    if (!f) return
    const bad = checkFile(f, 'logo')
    if (bad) return files.setError(bad)
    await files.run(() => uploadFile(p.id, 'logo', f))
  }

  async function onImages(e: ChangeEvent<HTMLInputElement>) {
    const list = [...(e.target.files ?? [])]
    e.target.value = ''
    const bad = list.map((f) => checkFile(f, 'images')).find(Boolean)
    if (bad) return files.setError(bad)
    if (p.images.length + list.length > SCRATCH_LIMITS.images) return files.setError(`A project holds at most ${SCRATCH_LIMITS.images} images.`)
    // One request per file, in order: the API refuses concurrent changes to the image list.
    await files.run(async () => {
      for (const [i, f] of list.entries()) {
        setProgress(`Uploading ${i + 1} of ${list.length}: ${f.name}`)
        await uploadFile(p.id, 'images', f).finally(() => setProgress(null))
      }
    })
  }

  const n = (key: string) => /\/images\/(\d+)-/.exec(key)?.[1] ?? ''

  return (
    <div className="flex max-w-4xl flex-col gap-3">
      <div className="flex flex-wrap items-start gap-3">
        <div className="mr-auto min-w-0">
          <Link to="/ops/scratch" className="text-xs text-muted-foreground underline-offset-4 hover:underline">
            Sites from scratch
          </Link>
          <h1 className="text-base font-semibold break-words">{p.businessName}</h1>
          <p className="text-muted-foreground">{p.domain}</p>
        </div>
        <Badge variant="outline">{p.status}</Badge>
      </div>

      <Section
        id="brief"
        title="Brief"
        actions={
          draft &&
          !editing && (
            <Button size="xs" variant="outline" onClick={() => setEditing(true)}>
              Edit
            </Button>
          )
        }
      >
        {editing ? (
          <ScratchForm initial={p} submitLabel="Save changes" pending={edit.pending} error={edit.error} onSubmit={save} onCancel={() => setEditing(false)} />
        ) : (
          <Facts
            items={[
              ['Domain', p.domain],
              ['Business name', p.businessName],
              ['Site type', SCRATCH_SITE_TYPES[p.siteType]?.label ?? p.siteType],
              [
                'Inspiration',
                <ul key="i" className="flex flex-col gap-0.5">
                  {p.inspirationUrls.map((u) => (
                    <li key={u} className="break-all">
                      <a href={u} target="_blank" rel="noopener noreferrer" className="underline underline-offset-4">
                        {u}
                      </a>
                    </li>
                  ))}
                </ul>,
              ],
              [
                'Colors',
                <ul key="c" className="flex flex-wrap gap-2">
                  {p.colors.map((c) => (
                    <li key={c} className="flex items-center gap-1.5 font-mono text-xs">
                      <span aria-hidden className="size-5 rounded border border-border" style={{ background: c }} />
                      {c}
                    </li>
                  ))}
                </ul>,
              ],
              ['Description', <p key="d" className="whitespace-pre-wrap">{p.description}</p>],
              ['Instructions', p.instructions ? <p key="n" className="whitespace-pre-wrap">{p.instructions}</p> : <span className="text-muted-foreground">None</span>],
              ['Created', `${formatDateTime(p.createdAt)} by ${p.createdBy}`],
              ['Updated', formatDateTime(p.updatedAt)],
            ]}
          />
        )}
      </Section>

      <Section id="logo" title="Logo">
        <div className="flex flex-wrap items-end gap-4">
          {p.logoKey ? (
            <img
              src={fileUrl(p, p.logoKey)}
              alt={`${p.businessName} logo`}
              className="h-24 max-w-48 rounded-md border border-border bg-[repeating-conic-gradient(var(--muted)_0_25%,transparent_0_50%)] bg-[length:16px_16px] object-contain p-1"
            />
          ) : (
            <Muted>No logo.</Muted>
          )}
          {draft && (
            <div className="flex flex-wrap items-end gap-2">
              <div className="flex flex-col gap-1.5">
                <Label htmlFor="logo-file">{p.logoKey ? 'Replace logo' : 'Upload logo'}</Label>
                <Input id="logo-file" type="file" accept={LOGO_ACCEPT} onChange={onLogo} disabled={files.pending} className="max-w-64" />
              </div>
              {p.logoKey && (
                <Button size="sm" variant="outline" disabled={files.pending} onClick={() => files.run(() => api(`${base}/logo`, { method: 'DELETE' }))}>
                  Remove logo
                </Button>
              )}
            </div>
          )}
        </div>
      </Section>

      <Section id="images" title={`Images (${p.images.length} of ${SCRATCH_LIMITS.images})`}>
        <div className="flex flex-col gap-3">
          {p.images.length === 0 ? (
            <Muted>No images.</Muted>
          ) : (
            <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
              {p.images.map((img) => (
                <li key={img.key} className="flex min-w-0 flex-col gap-1">
                  <a href={fileUrl(p, img.key)} target="_blank" rel="noopener noreferrer">
                    <img src={fileUrl(p, img.key)} alt={img.name} loading="lazy" className="aspect-square w-full rounded-md border border-border object-cover" />
                  </a>
                  <div className="flex items-center gap-1">
                    <span className="min-w-0 flex-1 truncate text-xs" title={img.name}>
                      {img.name} <span className="text-muted-foreground">({formatBytes(img.size)})</span>
                    </span>
                    {draft && (
                      <Button
                        size="xs"
                        variant="ghost"
                        disabled={files.pending}
                        aria-label={`Delete ${img.name}`}
                        onClick={() => files.run(() => api(`${base}/images/${n(img.key)}`, { method: 'DELETE' }))}
                      >
                        Delete
                      </Button>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
          {draft && p.images.length < SCRATCH_LIMITS.images && (
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="image-files">Add images</Label>
              <Input id="image-files" type="file" accept={IMAGE_ACCEPT} multiple onChange={onImages} disabled={files.pending} className="max-w-80" aria-describedby="image-files-hint" />
              <p id="image-files-hint" className="text-xs text-muted-foreground">
                PNG, JPEG, WebP or GIF, {SCRATCH_LIMITS.imageBytes / 1024 / 1024} MB each.
              </p>
            </div>
          )}
          {progress && (
            <p role="status" className="text-sm text-muted-foreground">
              {progress}
            </p>
          )}
        </div>
      </Section>
      <FormError message={files.error} />
    </div>
  )
}
